import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Heart, MoreVertical, ArrowLeft, Shuffle, Share2, Trash2 } from 'lucide-react';
import SongMenu from '../SongMenu';
import SharePlaylistModal from '../SharePlaylistModal';

const PlaylistPage = ({ songs, playSong, toggleLike, likedSongs, user }) => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [playlistSongs, setPlaylistSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    fetchPlaylist();
  }, [playlistId, songs]); // Also refresh when songs change

  const fetchPlaylist = async () => {
    try {
      // Get all user playlists
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/me`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      
      if (response.ok) {
        const playlists = await response.json();
        const found = playlists.find(p => p.id === playlistId);
        
        if (found) {
          setPlaylist(found);
          
          // Get songs in playlist, maintaining the order from song_ids
          const songMap = new Map(songs.map(s => [s.id, s]));
          const songList = (found.song_ids || [])
            .map(id => songMap.get(id))
            .filter(song => song !== undefined);
          setPlaylistSongs(songList);
        }
      }
    } catch (error) {
      console.error('Failed to fetch playlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex === null || draggedIndex === index) return;
    
    // Create a new array with the dragged item moved to the new position
    const newSongs = [...playlistSongs];
    const draggedSong = newSongs[draggedIndex];
    newSongs.splice(draggedIndex, 1);
    newSongs.splice(index, 0, draggedSong);
    
    setPlaylistSongs(newSongs);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    
    setDraggedIndex(null);
    setReordering(true);
    
    try {
      // Save the new order to backend
      const songIds = playlistSongs.map(song => song.id);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}/reorder`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(songIds)
        }
      );
      
      if (!response.ok) {
        console.error('Failed to save playlist order');
        // Revert to original order if save fails
        await fetchPlaylist();
      }
    } catch (error) {
      console.error('Error saving playlist order:', error);
      await fetchPlaylist();
    } finally {
      setReordering(false);
    }
  };

  const playPlaylist = () => {
    if (playlistSongs.length > 0) {
      playSong(playlistSongs[0], playlistSongs);
    }
  };

  const shufflePlaylist = () => {
    if (playlistSongs.length > 0) {
      // Create shuffled copy of playlist
      const shuffled = [...playlistSongs].sort(() => Math.random() - 0.5);
      playSong(shuffled[0], shuffled);
    }
  };

  const handleToggleShare = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}/share`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Update playlist with new share state
        setPlaylist(prev => ({
          ...prev,
          is_public: result.is_public,
          share_token: result.share_token
        }));
      } else {
        alert('Failed to toggle playlist sharing');
      }
    } catch (error) {
      console.error('Error toggling share:', error);
      alert('Failed to toggle playlist sharing');
    }
  };

  const handleDeletePlaylist = async () => {
    if (!confirm(`Are you sure you want to delete "${playlist?.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        alert('Playlist deleted successfully');
        
        // Trigger playlist refresh in sidebar
        window.dispatchEvent(new Event('playlistsChanged'));
        
        navigate('/library');
      } else {
        alert('Failed to delete playlist');
      }
    } catch (error) {
      console.error('Error deleting playlist:', error);
      alert('Failed to delete playlist');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="page-content">Loading playlist...</div>;
  }

  if (!playlist) {
    return (
      <div className="page-content">
        <div className="error-state">
          <h2>Playlist not found</h2>
          <button onClick={() => navigate('/')} className="back-home-btn">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <button className="back-button" onClick={() => navigate(-1)}>
        <ArrowLeft size={20} />
        Back
      </button>

      <div className="playlist-header-background">
        <div className="playlist-header-content">
          <div className="playlist-header-info">
            <span className="playlist-type">PLAYLIST</span>
            <h1 className="playlist-title">{playlist.name}</h1>
            <p className="playlist-description">{playlist.description || 'Your custom playlist'}</p>
            <div className="playlist-stats">
              <span>{playlistSongs.length} songs</span>
            </div>
          </div>
          <div className="playlist-cover">
            🎵
          </div>
        </div>
      </div>

      <div className="playlist-actions" style={{ display: 'flex', gap: '16px', padding: '0 40px 24px', alignItems: 'center' }}>
        <button 
          className="action-btn primary"
          onClick={playPlaylist}
          disabled={playlistSongs.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '16px 40px',
            borderRadius: '24px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: playlistSongs.length === 0 ? 'not-allowed' : 'pointer',
            background: '#9D4EDD',
            color: '#fff',
            opacity: playlistSongs.length === 0 ? 0.5 : 1
          }}
        >
          <Play size={20} fill="white" />
          Play
        </button>
        <button 
          className="action-btn shuffle-btn"
          onClick={shufflePlaylist}
          disabled={playlistSongs.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 32px',
            borderRadius: '24px',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            fontSize: '16px',
            fontWeight: '600',
            cursor: playlistSongs.length === 0 ? 'not-allowed' : 'pointer',
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            opacity: playlistSongs.length === 0 ? 0.5 : 1
          }}
        >
          <Shuffle size={20} />
          Shuffle
        </button>
        <button 
          className="action-btn share-btn"
          onClick={() => setShowShareModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 32px',
            borderRadius: '24px',
            border: '2px solid rgba(157, 78, 221, 0.5)',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            background: 'rgba(157, 78, 221, 0.1)',
            color: '#9D4EDD'
          }}
        >
          <Share2 size={20} />
          Share
        </button>
        <button 
          className="action-btn delete-btn"
          onClick={handleDeletePlaylist}
          disabled={deleting}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 32px',
            borderRadius: '24px',
            border: '2px solid rgba(239, 68, 68, 0.5)',
            fontSize: '16px',
            fontWeight: '600',
            cursor: deleting ? 'not-allowed' : 'pointer',
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            opacity: deleting ? 0.6 : 1
          }}
        >
          <Trash2 size={20} />
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>

      <div className="playlist-songs">
        {playlistSongs.length === 0 ? (
          <div className="empty-playlist">
            <h3>This playlist is empty</h3>
            <p>Add songs using the menu (⋮) on any song card</p>
          </div>
        ) : (
          <>
            <div className="songs-header">
              <div className="songs-header-index">#</div>
              <div className="songs-header-image"></div>
              <div className="songs-header-title">Title</div>
              <div className="songs-header-album">Album</div>
              <div className="songs-header-duration">Duration</div>
              <div className="songs-header-actions"></div>
            </div>

            <div className="songs-list">
              {playlistSongs.map((song, index) => (
                <div 
                  key={song.id} 
                  className={`song-row ${draggedIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{
                    cursor: 'grab',
                    opacity: draggedIndex === index ? 0.5 : 1,
                    transition: 'opacity 0.2s'
                  }}
                >
                  <div className="song-row-index" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    cursor: 'grab'
                  }}>
                    <span style={{ color: '#666', fontSize: '18px' }}>⋮⋮</span>
                    <span>{index + 1}</span>
                  </div>
                  <div className="song-row-image">
                    <img src={song.albumArt || song.album_art_url} alt={song.title} />
                    <button 
                      className="row-play-button"
                      onClick={() => playSong(song, playlistSongs)}
                    >
                      <Play size={16} />
                    </button>
                  </div>
                  <div className="song-row-info">
                    <div className="song-row-title">{song.title}</div>
                    <div className="song-row-artist">{song.artist}</div>
                  </div>
                  <div className="song-row-album">{song.album}</div>
                  <div className="song-row-duration">
                    {Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}
                  </div>
                  <div className="song-row-actions">
                    <button 
                      className={`song-row-like ${likedSongs.has(song.id) ? 'liked' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLike(song.id);
                      }}
                    >
                      <Heart size={16} fill={likedSongs.has(song.id) ? '#9D4EDD' : 'none'} />
                    </button>
                    <div onClick={(e) => e.stopPropagation()}>
                      <SongMenu song={song} user={user} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {reordering && (
              <div style={{
                padding: '12px',
                textAlign: 'center',
                color: '#9D4EDD',
                fontSize: '14px'
              }}>
                Saving order...
              </div>
            )}
          </>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <SharePlaylistModal
          playlist={playlist}
          onClose={() => setShowShareModal(false)}
          onToggleShare={handleToggleShare}
        />
      )}
    </div>
  );
};

export default PlaylistPage;
